import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { RefreshCw, TrendingUp, TrendingDown, Clock, ZapOff, LineChart as LineChartIcon, Share2 } from "lucide-react";
import { formatRelativeShortEs } from "@/lib/relativeTime";
import { toast } from "sonner";
import PriceHistoryDialog from "@/components/PriceHistoryDialog";
import WatchlistShareDialog from "@/components/WatchlistShareDialog";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const TYPE_LABEL = { stock: "Acciones", fund: "Fondos", crypto: "Cripto" };

export default function Watchlist() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [allInvestments, setAllInvestments] = useState([]);

  const load = useCallback(async () => {
    try {
      const [wl, inv] = await Promise.all([
        api.get("/investments/watchlist"),
        api.get("/investments"),
      ]);
      setItems(wl.data?.items || []);
      setAllInvestments(inv.data || []);
    } catch (e) {
      logger.error("watchlist load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/investments/refresh-all");
      const total = r.data?.total_updated ?? 0;
      if (total > 0) toast.success(t("watchlistPage.refreshed", { count: total, defaultValue: "Prices updated ({{count}})" }));
      else toast.info(t("watchlistPage.noChanges", { defaultValue: "No changes" }));
      load();
    } catch (e) {
      logger.error("watchlist refresh", e);
      toast.error(t("watchlistPage.refreshFailed", { defaultValue: "Couldn't refresh" }));
    } finally {
      setRefreshing(false);
    }
  };

  const openHistory = (row) => {
    const full = allInvestments.find((i) => i.id === row.id) || row;
    setHistoryItem(full);
    setHistoryOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-bold text-[#8A9992]">{t("watchlistPage.kicker", { defaultValue: "Portfolio" })}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0A110E]">{t("watchlistPage.title")}</h1>
          <p className="text-sm text-[#5F7368] mt-1">
            {t("watchlistPage.subtitle", { defaultValue: "Cotizaciones y variaciones diarias en un vistazo. Ordenado por movimiento del día." })}
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              data-testid="watchlist-share-btn"
              variant="outline"
              onClick={() => setShareOpen(true)}
              className="rounded-xl border-[#EBEBEA]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {t("watchlistPage.share")}
            </Button>
            <Button
              data-testid="watchlist-refresh-btn"
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              className="rounded-xl border-[#EBEBEA]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        )}
      </div>

      <div className="card-surface overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.08em] text-[#8A9992] font-bold border-b border-[#EBEBEA]">
          <div className="col-span-4">Activo</div>
          <div className="col-span-2 text-right">Precio</div>
          <div className="col-span-2 text-right">Δ hoy</div>
          <div className="col-span-2 text-right">Valor</div>
          <div className="col-span-2 text-right">Actualizado</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#8A9992]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-[#8A9992]" data-testid="watchlist-empty">
            No tienes acciones, fondos ni cripto todavía.
          </div>
        ) : (
          items.map((it) => {
            const pct = it.change_pct;
            const up = (pct ?? 0) >= 0;
            return (
              <button
                key={it.id}
                type="button"
                data-testid={`watchlist-row-${it.id}`}
                onClick={() => openHistory(it)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-[#EBEBEA] last:border-b-0 hover:bg-[#FAFAF9] text-left transition-colors"
              >
                <div className="col-span-4 min-w-0">
                  <div className="flex items-center gap-2">
                    <LineChartIcon className="w-3.5 h-3.5 text-[#1C7C54] shrink-0" />
                    <span className="text-sm font-medium text-[#0A110E] truncate">{it.name}</span>
                  </div>
                  <div className="text-[11px] text-[#8A9992] mt-0.5 truncate">
                    {TYPE_LABEL[it.type] || it.type}
                    {it.ticker && <span> · {it.ticker.toUpperCase()}</span>}
                    {it.isin && <span> · {it.isin}</span>}
                  </div>
                </div>
                <div className="col-span-2 text-right text-sm font-semibold tabular-nums text-[#0A110E]">
                  {formatCurrency(it.current_price, it.currency)}
                </div>
                <div className="col-span-2 text-right tabular-nums" data-testid={`watchlist-delta-${it.id}`}>
                  {pct == null ? (
                    <span className="text-[#8A9992] text-sm">—</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${up ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
                      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {up ? "+" : ""}{pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right text-sm tabular-nums text-[#5F7368]">
                  {formatCurrency(it.value, it.currency)}
                </div>
                <div className="col-span-2 text-right text-[11px] text-[#8A9992] flex items-center justify-end gap-1">
                  {it.is_stale ? (
                    <ZapOff className="w-3 h-3 text-[#D95D39]" />
                  ) : (
                    <Clock className="w-3 h-3" />
                  )}
                  {it.price_updated_at ? formatRelativeShortEs(it.price_updated_at) : "sin datos"}
                </div>
              </button>
            );
          })
        )}
      </div>

      <PriceHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        investment={historyItem}
        baseCurrency={user?.base_currency}
        allInvestments={allInvestments}
      />
      <WatchlistShareDialog open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}
